import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Archive as ArchiveIcon, ArchiveRestore, CheckCircle2, ChevronDown, ChevronRight, Clock, Loader2, XCircle, Ban, RefreshCw, SendHorizonal } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { CodeBlock, langForFilename } from "@/components/CodeBlock";
import { DiffText } from "@/components/DiffText";
import { useAuth } from "@/context/AuthContext";
import { useOrg } from "@/hooks/useOrg";
import { kindBadge } from "@/lib/kinds";
import { api } from "@/lib/api";

const STATUS_BADGE = {
  published: { icon: CheckCircle2, cls: "bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]", label: "Published" },
  pending: { icon: Clock, cls: "bg-[color:var(--rb-gold-soft)] text-[color:var(--rb-gold)]", label: "Pending" },
  rejected: { icon: XCircle, cls: "bg-[color:rgba(248,113,113,0.14)] text-[color:#f87171]", label: "Rejected" },
  yanked: { icon: Ban, cls: "border border-[color:var(--rb-border)] text-[color:var(--rb-text-faint)]", label: "Yanked" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_BADGE[status] || STATUS_BADGE.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] ${cfg.cls}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

export default function OrgArtifact() {
  const { slug, id } = useParams();
  const { user } = useAuth();
  const { org } = useOrg(slug);
  const [artifact, setArtifact] = useState(null);
  const [versions, setVersions] = useState(null);

  const load = useCallback(async () => {
    if (!slug || !id) return;
    try {
      const [a, v] = await Promise.all([
        api.get(`/orgs/${slug}/artifacts/${id}`),
        api.get(`/orgs/${slug}/artifacts/${id}/versions`),
      ]);
      setArtifact(a.data);
      setVersions(v.data);
    } catch (e) {
      setArtifact(false);
    }
  }, [slug, id]);

  useEffect(() => {
    load();
  }, [load]);

  const isCreator = artifact && user && artifact.creator_id === user.user_id;
  const isAdmin = org && (org.role === "owner" || org.role === "admin");
  const latestRejection = versions?.find((v) => v.status === "rejected");

  const [openDiff, setOpenDiff] = useState(null); // version_id of the expanded row
  const [diffs, setDiffs] = useState({}); // version_id -> {files: [...]}
  const reviewed = (v) => v && ["published", "yanked"].includes(v.status);
  // Members may only diff reviewed rows (the API enforces it); admins may diff anything.
  const canDiff = (v) => {
    if (v.version < 2) return false;
    if (isAdmin) return true;
    const prev = (versions || []).find((p) => p.version === v.version - 1);
    return reviewed(v) && reviewed(prev);
  };
  const toggleDiff = async (v) => {
    if (openDiff === v.id) return setOpenDiff(null);
    setOpenDiff(v.id);
    if (!diffs[v.id]) {
      try {
        const { data } = await api.get(`/orgs/${slug}/versions/${v.id}/diff`);
        setDiffs((d) => ({ ...d, [v.id]: data }));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Couldn't load the diff");
      }
    }
  };
  const republish = async (v) => {
    if (!window.confirm(`Republish v${v.version}? The currently published version is superseded and everyone's next sync reverts to this one.`)) return;
    try {
      await api.post(`/orgs/${slug}/versions/${v.id}/republish`, { note: "reverted from artifact page" });
      toast.success(`v${v.version} is live again — members' next sync picks it up`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Couldn't republish");
    }
  };
  const toggleArchive = async () => {
    const archiving = !artifact.archived;
    if (archiving && !window.confirm(`Archive ${artifact.slug}? Its published version is pulled and members' next sync removes it. Nothing is deleted.`)) return;
    try {
      await api.post(`/skills/${id}/${archiving ? "archive" : "unarchive"}`);
      toast.success(archiving ? "Archived — pulled from the catalog" : "Unarchived — republish a version to bring it back");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  if (artifact === null || versions === null) {
    return <AppShell><div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-[color:var(--rb-text-faint)]" /></div></AppShell>;
  }
  if (artifact === false) {
    return <AppShell><div className="py-24 text-center text-sm text-[color:var(--rb-text-faint)]">Artifact not found.</div></AppShell>;
  }

  return (
    <AppShell>
      <Link to={`/orgs/${slug}`} className="inline-flex items-center gap-1 text-xs text-[color:var(--rb-text-faint)] hover:text-[color:var(--rb-text)]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to catalog
      </Link>

      <div className="mt-3 flex items-center gap-2">
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">{artifact.name}</h1>
        {kindBadge(artifact.kind) && (
          <span className="rounded-full border border-[color:var(--rb-border)] px-2 py-0.5 text-[10px] text-[color:var(--rb-text-faint)]">{kindBadge(artifact.kind)}</span>
        )}
        {artifact.archived ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-[color:var(--rb-border)] px-2 py-0.5 text-[10px] text-[color:var(--rb-text-faint)]">
            <ArchiveIcon className="h-3 w-3" /> Archived
          </span>
        ) : artifact.published_version ? <StatusBadge status="published" /> : null}
        {(isCreator || isAdmin) && (
          <button onClick={toggleArchive} data-testid="org-artifact-archive-toggle"
            title={artifact.archived ? "Bring it back to the working set" : "Retire it — pulled from the catalog, never deleted"}
            className="ml-auto flex items-center gap-1 rounded-lg border border-[color:var(--rb-border)] px-2 py-1 text-xs text-[color:var(--rb-text-dim)] hover:border-[color:var(--rb-border-strong)] hover:text-[color:var(--rb-text)]">
            {artifact.archived ? <><ArchiveRestore className="h-3.5 w-3.5" /> Unarchive</> : <><ArchiveIcon className="h-3.5 w-3.5" /> Archive</>}
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">by @{artifact.creator_name} · {artifact.short_description || "No description yet."}</p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        {/* min-w-0: grid children default to min-width:auto, so long code lines would widen
            the 1fr track (card grows with the text) instead of scrolling inside CodeBlock. */}
        <div className="min-w-0">
          {artifact.published_version ? (
            <div className="overflow-hidden rounded-2xl border border-[color:var(--rb-border)]">
              <div className="border-b border-[color:var(--rb-border)] bg-[color:var(--rb-code)] px-4 py-2 text-xs text-[color:var(--rb-text-faint)]">
                Published content — v{artifact.published_version.version}
              </div>
              <CodeBlock code={artifact.published_version.skill_md} language={langForFilename("SKILL.md")} />
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-[color:var(--rb-border)] p-8 text-center text-sm text-[color:var(--rb-text-faint)]">
              Nothing published yet — this artifact hasn't cleared review.
            </div>
          )}

          {latestRejection && (
            <div className="mt-4 rounded-2xl border border-[color:rgba(248,113,113,0.4)] bg-[color:rgba(248,113,113,0.06)] p-3 text-xs" data-testid="rejection-note">
              <div className="font-medium text-[color:#f87171]">Most recent rejection (v{latestRejection.version})</div>
              {latestRejection.rejection_note && <p className="mt-1 text-[color:var(--rb-text-dim)]">{latestRejection.rejection_note}</p>}
              {(latestRejection.scan?.reasons || []).slice(0, 5).map((r, i) => (
                <p key={i} className="mt-0.5 text-[color:var(--rb-text-faint)]">· {r}</p>
              ))}
            </div>
          )}

          <div className="mt-6">
            <h2 className="text-sm font-medium text-[color:var(--rb-text)]">Version history</h2>
            <div className="mt-2 space-y-2">
              {(versions || []).length === 0 ? (
                <p className="text-xs text-[color:var(--rb-text-faint)]">No versions submitted yet.</p>
              ) : (
                versions.map((v) => (
                  <div key={v.id} data-testid="version-row" className="rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-xs font-medium text-[color:var(--rb-text)]">
                          {canDiff(v) ? (
                            <button onClick={() => toggleDiff(v)} data-testid={`org-version-diff-toggle-${v.version}`}
                              className="flex items-center gap-1 text-[color:var(--rb-text-dim)] hover:text-[color:var(--rb-text)]">
                              {openDiff === v.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                              v{v.version}
                            </button>
                          ) : (
                            <span>v{v.version}</span>
                          )}
                          <StatusBadge status={v.status} />
                        </div>
                        {v.changelog && <p className="mt-1 text-xs text-[color:var(--rb-text-dim)]">{v.changelog}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {isAdmin && v.status === "yanked" && !v.platform_killed && (
                          <button onClick={() => republish(v)} data-testid={`org-version-republish-${v.version}`}
                            title="Make this version live again — members' next sync reverts to it"
                            className="flex items-center gap-1 rounded-lg border border-[color:var(--rb-border)] px-2 py-0.5 text-[10px] text-[color:var(--rb-text-dim)] hover:border-[color:var(--rb-border-strong)] hover:text-[color:var(--rb-text)]">
                            <RefreshCw className="h-3 w-3" /> Republish
                          </button>
                        )}
                        <span className="text-[10px] text-[color:var(--rb-text-faint)]">{(v.created_at || "").slice(0, 10)}</span>
                      </div>
                    </div>
                    {openDiff === v.id && (
                      diffs[v.id] ? (
                        diffs[v.id].files.length ? diffs[v.id].files.map((f) => (
                          <div key={f.filename} className="mt-2">
                            <div className="rb-mono text-xs text-[color:var(--rb-text-faint)]">{f.filename} <span className="text-[color:var(--rb-text-dim)]">({f.status})</span></div>
                            <DiffText text={f.diff} />
                          </div>
                        )) : <p className="mt-2 text-xs text-[color:var(--rb-text-faint)]">No content changes in this version.</p>
                      ) : <Loader2 className="mt-2 h-4 w-4 animate-spin text-[color:var(--rb-text-faint)]" />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {isCreator && !artifact.archived && (
          <aside className="h-fit rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-4">
            <div className="flex items-center gap-1.5 text-sm font-medium text-[color:var(--rb-text)]">
              <SendHorizonal className="h-4 w-4 text-[color:var(--rb-accent)]" /> Ship an update
            </div>
            <p className="mt-1 text-xs text-[color:var(--rb-text-faint)]">
              Edit the content, note what changed and why, and submit — it goes to
              {org?.approval_mode === "auto_clean" ? " auto-approval (clean scans publish immediately)." : " your admin's queue."}
            </p>
            <Link to={`/skill/${id}/edit`} data-testid="org-artifact-modify-link"
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl bg-[color:var(--rb-accent)] px-3 py-2 text-sm font-medium text-[color:var(--rb-void)] hover:bg-[color:var(--rb-accent-2)]">
              Modify
            </Link>
          </aside>
        )}
      </div>
    </AppShell>
  );
}
